import { useEffect, useState, type ReactElement } from "react";
import { CheckCircle2, Circle } from "lucide-react";
import { color, space } from "@tokens/index";
import { Badge, Button, SkeletonRows, Surface, Text, useViewport } from "@primitives/index";
import { QueueWorkItemsPager, staleDataStyle } from "@compositions/index";
import { AdminSection, EmptyLine, ErrorAlert } from "../admin-ui";
import type { WorkItem } from "../workflows";
import { useWorkItemsByQueue } from "../workitems";
import type { CompletedWorkItemPageSize, QueueWorkItemView } from "../workitems";

/**
 * Work items that belong to a queue, with an active/completed toggle and
 * paging for the completed view.
 */

function WorkItemRow({ item }: { readonly item: WorkItem }): ReactElement {
    const { isCompact } = useViewport();
    const isCompleted = item.completedAt !== null;
    const tagEntries = Object.entries(item.routingTags);
    const createdBy = item.createdByDisplayName ?? item.createdBy;
    return (
        <Surface
            elevation={1}
            borderRadius="lg"
            padding={isCompact ? 3 : 4}
            style={{ width: "100%", minWidth: 0 }}
        >
            <div style={{ display: "grid", gap: space[2], minWidth: 0 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: space[2],
                        flexWrap: "wrap",
                    }}
                >
                    {isCompleted ? (
                        <CheckCircle2
                            size={16}
                            strokeWidth={2.25}
                            aria-hidden="true"
                            color={color.accent.primary}
                        />
                    ) : (
                        <Circle
                            size={16}
                            strokeWidth={2.25}
                            aria-hidden="true"
                            color={color.fg.muted}
                        />
                    )}
                    <Badge variant="accent" size="sm" caps={false}>
                        {item.type}
                    </Badge>
                    {item.dueDate !== null && (
                        <Badge variant="warning" size="sm">
                            {`DUE ${new Date(item.dueDate).toLocaleString(undefined, {
                                month: "short",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                            })}`}
                        </Badge>
                    )}
                    {isCompleted && (
                        <Badge variant="neutral" size="sm">
                            COMPLETED
                        </Badge>
                    )}
                </div>
                <Text as="p" role="body" style={{ overflowWrap: "anywhere" }}>
                    {item.body}
                </Text>
                <Text role="caption" tone="muted" truncate>
                    Created {new Date(item.createdAt).toLocaleString()}
                    {createdBy !== null && ` by ${createdBy}`}
                </Text>
                {tagEntries.length > 0 && (
                    <div style={{ display: "flex", gap: space[2], flexWrap: "wrap" }}>
                        {tagEntries.map(([k, v]) => (
                            <Badge key={k} variant="default" size="sm" caps={false}>
                                {`${k}: ${v}`}
                            </Badge>
                        ))}
                    </div>
                )}
            </div>
        </Surface>
    );
}

interface QueueWorkItemsSectionProps {
    readonly queueId: string;
}

export function QueueWorkItemsSection({ queueId }: QueueWorkItemsSectionProps): ReactElement {
    const [workItemView, setWorkItemView] = useState<QueueWorkItemView>("active");
    const [completedSkip, setCompletedSkip] = useState(0);
    const [completedTake, setCompletedTake] = useState<CompletedWorkItemPageSize>(20);
    useEffect(() => {
        setCompletedSkip(0);
    }, [queueId]);
    const query = useWorkItemsByQueue(
        workItemView === "completed"
            ? {
                  queueId,
                  view: "completed",
                  skip: completedSkip,
                  take: completedTake,
              }
            : { queueId, view: "active" },
    );

    const items = query.data?.items ?? [];
    const total = query.data?.totalCount ?? 0;
    const isCompletedView = workItemView === "completed";
    // Skeleton only before the first result. A view or page change keeps the
    // previous rows on screen, dimmed, until the next page arrives.
    const showSkeleton = query.data === undefined && query.error === null;

    return (
        <AdminSection
            title="Work items"
            description={`${String(total)} ${isCompletedView ? "completed" : "active"} item${total === 1 ? "" : "s"}`}
            trailing={
                <Button
                    variant={isCompletedView ? "soft" : "ghost"}
                    size="sm"
                    aria-pressed={isCompletedView}
                    onClick={() => {
                        setCompletedSkip(0);
                        setWorkItemView((current) =>
                            current === "active" ? "completed" : "active",
                        );
                    }}
                >
                    {isCompletedView ? "Show active" : "Show completed"}
                </Button>
            }
        >
            {showSkeleton && (
                <SkeletonRows rows={3} rowHeight={88} shape="card" label="Loading work items" />
            )}
            {query.error !== null && (
                <ErrorAlert onRetry={() => void query.refetch()}>
                    Couldn&rsquo;t load work items. {query.error.message}
                </ErrorAlert>
            )}
            {query.data !== undefined && query.error === null && items.length === 0 && (
                <EmptyLine>
                    {isCompletedView
                        ? "No completed work items are visible to this queue."
                        : "No active work items. Show completed to review queue history."}
                </EmptyLine>
            )}
            {items.length > 0 && (
                <div style={{ display: "grid", gap: space[4], ...staleDataStyle(query.isPlaceholderData) }}>
                    {items.map((item) => (
                        <WorkItemRow key={item.id} item={item} />
                    ))}
                </div>
            )}
            {isCompletedView && total > 0 && (
                <QueueWorkItemsPager
                    skip={completedSkip}
                    take={completedTake}
                    itemCount={items.length}
                    totalCount={total}
                    onPageChange={setCompletedSkip}
                    onPageSizeChange={(take) => {
                        setCompletedTake(take);
                        setCompletedSkip(0);
                    }}
                />
            )}
        </AdminSection>
    );
}
